import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Liefert Boersen-Marktdaten (EPEX) — oder einen ehrlichen Fehler.
 *
 * WICHTIG: Dieser Hook gibt NIEMALS erfundene Zahlen zurueck.
 * Frueher wurde bei jedem Fehler `api.getFallbackMarketData()` gesetzt. Diese
 * Fallback-Daten stammen aus generateRealisticPrice() (enthaelt Math.random())
 * plus hart kodierten Analysewerten. Ein Vertriebler haette diese frei
 * erfundene Zahl als "Boersenpreis EPEX" im Kundengespraech genannt.
 *
 * Die Daten kommen jetzt vom eigenen Server (`/api/spotpreise`) statt direkt aus
 * dem Browser. Grund: die Boersen-APIs (energy-charts, SMARD, aWATTar) erlauben
 * keine Cross-Origin-Abrufe — im Browser scheiterte JEDER Abruf an CORS, weshalb
 * de facto immer die simulierten Werte angezeigt wurden. Serverseitig sind es
 * echte EPEX-Daten, zwischengespeichert und ohne aufgefuellte Luecken.
 *
 * Rueckgabe:
 *   marketData — echte Marktdaten oder null. null heisst: keine belastbare Zahl.
 *   error      — Fehlermeldung (string) oder null.
 *   isLoading  — laeuft ein Abruf.
 *   refetch    — erneut versuchen.
 */
export const useMarketData = () => {
  const [marketData, setMarketData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const resp = await fetch('/api/spotpreise');
      const data = await resp.json().catch(() => null);

      if (!isMounted.current) return;

      if (!resp.ok || !data || data.error) {
        setMarketData(null);
        setError('Boersendaten sind derzeit nicht abrufbar.');
        return;
      }

      if (typeof data.basePrice !== 'number' || !Number.isFinite(data.basePrice)) {
        setMarketData(null);
        setError('Marktdaten sind unvollstaendig (kein gueltiger Boersenpreis).');
        return;
      }

      setMarketData(data);
      setError(null);
    } catch (err) {
      if (!isMounted.current) return;
      // Kein Fallback: lieber gar keine Zahl als eine erfundene.
      setMarketData(null);
      setError(err?.message || 'Marktdaten konnten nicht geladen werden.');
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    fetchData();
    const interval = setInterval(fetchData, 15 * 60 * 1000); // Alle 15 Min
    return () => {
      isMounted.current = false;
      clearInterval(interval);
    };
  }, [fetchData]);

  return { marketData, isLoading, error, refetch: fetchData };
};

export default useMarketData;
